import { useState } from "react";
import { isAxiosError } from "axios";
import { useAuth } from "@/hooks/useAuth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Loader2, ShieldAlert } from "lucide-react";
import toast from "react-hot-toast";
import { axiosClient } from "@/lib/axiosClient";

// Popup paksa ganti password untuk role_id 5 (Petugas Presensi) yang akunnya
// dibuat otomatis (import Excel / Set Moderator) dengan password default
// "Konaspi2026!" dan belum pernah menggantinya (is_changepass === 0).
export default function ForceChangePasswordDialog() {
    const { user, setUser } = useAuth();
    const [passwordData, setPasswordData] = useState({
        current_password: "",
        new_password: "",
        new_password_confirmation: "",
    });
    const [isSaving, setIsSaving] = useState(false);

    const shouldForce = !!user && user.role_id === 5 && !user.is_changepass;

    const handleSubmit = async () => {
        if (!passwordData.current_password || !passwordData.new_password || !passwordData.new_password_confirmation) {
            toast.error("Semua kolom wajib diisi.");
            return;
        }
        if (passwordData.new_password !== passwordData.new_password_confirmation) {
            toast.error("Konfirmasi password baru tidak cocok.");
            return;
        }
        if (passwordData.new_password === "Konaspi2026!") {
            toast.error("Password baru tidak boleh sama dengan password default.");
            return;
        }

        try {
            setIsSaving(true);
            await axiosClient.post("/users/change-password", passwordData);
            toast.success("Password berhasil diubah!");
            if (user) {
                setUser({ ...user, is_changepass: 1 });
            }
            setPasswordData({ current_password: "", new_password: "", new_password_confirmation: "" });
        } catch (error) {
            const message = isAxiosError(error) ? error.response?.data?.message : undefined;
            toast.error(message || "Gagal mengubah password.");
        } finally {
            setIsSaving(false);
        }
    };

    if (!shouldForce) return null;

    return (
        <Dialog open={shouldForce} onOpenChange={() => { /* tidak bisa ditutup sebelum password diganti */ }}>
            <DialogContent
                className="sm:max-w-[420px] bg-white dark:bg-slate-900 border-slate-200 dark:border-slate-800 rounded-3xl p-6 shadow-2xl"
                showCloseButton={false}
                onInteractOutside={(e) => e.preventDefault()}
                onEscapeKeyDown={(e) => e.preventDefault()}
            >
                <DialogHeader>
                    <div className="mx-auto sm:mx-0 mb-2 flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400">
                        <ShieldAlert className="w-5 h-5" />
                    </div>
                    <DialogTitle className="text-xl font-bold text-slate-900 dark:text-white">Ganti Password Anda</DialogTitle>
                    <DialogDescription className="text-sm text-slate-500 dark:text-slate-400">
                        Akun Anda masih menggunakan password default. Untuk keamanan, silakan ganti password terlebih dahulu sebelum melanjutkan.
                    </DialogDescription>
                </DialogHeader>
                <div className="grid gap-4 py-4">
                    <div className="space-y-2">
                        <Label htmlFor="force_current_password" className="text-slate-700 dark:text-slate-300 font-semibold">Password Saat Ini</Label>
                        <Input
                            id="force_current_password"
                            type="password"
                            autoComplete="current-password"
                            value={passwordData.current_password}
                            onChange={e => setPasswordData({ ...passwordData, current_password: e.target.value })}
                            className="h-11 rounded-xl bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 focus-visible:ring-amber-500"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="force_new_password" className="text-slate-700 dark:text-slate-300 font-semibold">Password Baru</Label>
                        <Input
                            id="force_new_password"
                            type="password"
                            autoComplete="new-password"
                            value={passwordData.new_password}
                            onChange={e => setPasswordData({ ...passwordData, new_password: e.target.value })}
                            className="h-11 rounded-xl bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 focus-visible:ring-amber-500"
                        />
                    </div>
                    <div className="space-y-2">
                        <Label htmlFor="force_new_password_confirmation" className="text-slate-700 dark:text-slate-300 font-semibold">Konfirmasi Password Baru</Label>
                        <Input
                            id="force_new_password_confirmation"
                            type="password"
                            autoComplete="new-password"
                            value={passwordData.new_password_confirmation}
                            onChange={e => setPasswordData({ ...passwordData, new_password_confirmation: e.target.value })}
                            className="h-11 rounded-xl bg-slate-50 dark:bg-slate-900/50 border-slate-200 dark:border-slate-800 focus-visible:ring-amber-500"
                        />
                    </div>
                </div>
                <DialogFooter>
                    <Button onClick={handleSubmit} disabled={isSaving} className="w-full bg-amber-600 hover:bg-amber-700 text-white">
                        {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                        Ganti Password
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
